import { Icon, Typography } from '@fox/atoms';
import { CentredFlexRow, FlexColumnSpaced, FlexRowSpaced, SpaceBetweenRow } from '@fox/layout';
import DOMPurify from 'dompurify';
import * as moment from 'moment';
import styled, { useTheme } from 'styled-components';
import { NewsListItemModel } from './contracts';

const EXTRA_INFO_FONT_COLOUR = '#6A6363';

const LeftImageNewsItemsWrapper = styled.div<{ $hideThumbNail: boolean }>`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(${props => (props.$hideThumbNail ? '32rem' : '50rem')}, max-content));
	padding: 2rem;
	padding-top: 2rem;
	grid-gap: 4rem;
	justify-content: center;
	align-items: flex-start;
	a {
		color: inherit;
	}
	.newsItem {
		background-color: #f9f9f9;
		border-radius: 20px;
		padding: 1rem;
		gap: 2rem;
		text-align: left;
		max-width: ${props => (props.$hideThumbNail ? '37rem' : '55rem')};
	}
	img {
		border-radius: 16px;
		min-width: 200px;
		width: 200px;
		height: 200px;
		object-fit: cover;
	}
	@media (max-width: 60rem) {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		padding: 1rem;
		gap: 2rem;
		.newsItem {
			width: 100%;
		}
		a {
			width: ${props => (props.$hideThumbNail ? '37rem' : '100%')};
		}
	}
	@media (max-width: 767px) {
		gap: 1rem;
		img {
			min-width: 100px;
			width: 100px;
			height: 100px;
		}
		.newsItem {
			gap: 1rem;
			padding: ${props => (props.$hideThumbNail ? '1rem' : '0.5rem')};
			max-width: 100%;
		}
		a {
			width: 100%;
		}
	}
`;

const ReadMoreWrapper = styled(SpaceBetweenRow)`
	@media (max-width: 767px) {
		display: none;
	}
`;

const TextWrapper = styled.div<{ $tagStringLength: number; $showFullReadMore?: boolean; $numberOfReadMoreLines?: number }>`
	display: flex;
	justify-content: space-between;
	flex-direction: column;
	width: 100%;
	gap: 1rem;
	height: 12.5rem;
	.read-more {
		${props =>
			props.$showFullReadMore && !props.$numberOfReadMoreLines ? '' : `-webkit-line-clamp: ${props.$numberOfReadMoreLines ?? 2};`}
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		max-width: 40rem;
		font-size: 1rem;
	}
	.title {
		-webkit-line-clamp: 2;
		overflow: hidden;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		max-width: 40rem;
		line-height: 1.688rem;
	}
	.mobileDate {
		display: none;
		white-space: nowrap;
		line-height: 1rem;
	}
	.desktopDate {
		display: block;
		white-space: nowrap;
		line-height: 1rem;
	}
	@media (max-width: 60rem) {
		.title {
			width: 100%;
		}
		.read-more {
			display: none;
		}
	}
	@media (max-width: 767px) {
		height: 6.25rem;
		.title {
			-webkit-line-clamp: ${props =>
				props.$tagStringLength <= 20 ? '4' : props.$tagStringLength <= 40 ? '3' : props.$tagStringLength <= 60 ? '2' : '1'};
			line-height: 1.1rem;
		}
		.mobileDate {
			display: block;
		}
		.desktopDate {
			display: none;
		}
		.authorAndDate {
			font-size: 0.75rem;
			gap: 0.25rem;
		}
		.author {
			display: none;
		}
	}
`;

function LeftImageNewsItem({
	newsItem,
	openLinkInNewTab,
	hideThumbnail,
	numberOfReadMoreLines,
	showFullReadMore,
	readMoreTextColour
}: {
	newsItem: NewsListItemModel;
	openLinkInNewTab: boolean;
	hideThumbnail?: boolean;
	showFullReadMore?: boolean;
	numberOfReadMoreLines?: number;
	readMoreTextColour?: string;
}) {
	const mobileDate = newsItem.publishDate ? moment(newsItem.publishDate).format('DD MMM YY') : null;
	const deskTopDate = newsItem.publishDate ? moment(newsItem.publishDate).format('DD MMMM YYYY') : null;
	const theme = useTheme();

	const tagString = newsItem.tags?.join(' | ') ?? '';
	return (
		<a href={newsItem.link} target={openLinkInNewTab ? '_blank' : '_self'}>
			<FlexRowSpaced className="newsItem">
				{!hideThumbnail ? <img src={newsItem.mobileThumbnailUrl || newsItem.thumbnailUrl} /> : null}
				<TextWrapper
					$tagStringLength={tagString.length}
					$showFullReadMore={showFullReadMore}
					$numberOfReadMoreLines={numberOfReadMoreLines}
				>
					<FlexColumnSpaced style={{ gap: '0.625rem' }}>
						<FlexRowSpaced className="authorAndDate">
							{newsItem?.tags?.length ? (
								<Typography sx={{ fontWeight: 'bold', lineHeight: '1rem' }} color="primary">
									{tagString}
								</Typography>
							) : null}
							{mobileDate ? (
								<Typography className="mobileDate" color={EXTRA_INFO_FONT_COLOUR}>
									{mobileDate}
								</Typography>
							) : null}
							{deskTopDate ? (
								<Typography className="desktopDate" color={EXTRA_INFO_FONT_COLOUR}>
									{deskTopDate}
								</Typography>
							) : null}
						</FlexRowSpaced>
						<Typography variant="h6" className="title">
							{newsItem.title}
						</Typography>
						{newsItem.readMoreSummary?.length || newsItem.summary?.length ? (
							showFullReadMore ? (
								<div
									className="read-more"
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(newsItem.readMoreSummary) || DOMPurify.sanitize(newsItem.summary)
									}}
								/>
							) : (
								<Typography className="read-more">{newsItem.readMoreSummary || newsItem.summary || ''}</Typography>
							)
						) : null}
					</FlexColumnSpaced>
					<ReadMoreWrapper>
						<div style={{ width: 'max-content' }}>
							{newsItem.author ? (
								<Typography className="author" color={EXTRA_INFO_FONT_COLOUR}>{`By ${newsItem.author}`}</Typography>
							) : null}
						</div>

						<CentredFlexRow style={{ width: 'max-content' }}>
							<Typography
								style={{
									fontWeight: 'bold',
									marginRight: '1rem'
								}}
								color={readMoreTextColour || theme.palette.primary.main}
							>
								Read More
							</Typography>
							<Icon iconName="ArrowForwardIos" customColor={readMoreTextColour || theme.palette.primary.main} />
						</CentredFlexRow>
					</ReadMoreWrapper>
				</TextWrapper>
			</FlexRowSpaced>
		</a>
	);
}

export function LeftImageView({
	openLinkInNewTab,
	newsItems,
	hideThumbnail,
	showFullReadMore,
	numberOfReadMoreLines,
	readMoreTextColour
}: {
	openLinkInNewTab: boolean;
	newsItems: NewsListItemModel[];
	hideThumbnail?: boolean;
	showFullReadMore?: boolean;
	numberOfReadMoreLines?: number;
	readMoreTextColour?: string;
}) {
	return (
		<LeftImageNewsItemsWrapper $hideThumbNail={hideThumbnail}>
			{newsItems?.map((newsItem, i) => {
				const id = `${i}${newsItem.truncatedTitle}`;
				return (
					<LeftImageNewsItem
						key={id}
						newsItem={newsItem}
						openLinkInNewTab={openLinkInNewTab}
						hideThumbnail={hideThumbnail}
						showFullReadMore={showFullReadMore}
						numberOfReadMoreLines={numberOfReadMoreLines}
						readMoreTextColour={readMoreTextColour}
					/>
				);
			})}
		</LeftImageNewsItemsWrapper>
	);
}
